import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { deviceIds } from 'src/embross-device-manager';
import {
  applyBagtagStatusCode,
  parseBagtagStatusIsOK,
} from '@app/cuss/embross/utils/callbacks/bagtag.callback';
import { CussStatusCode } from '@app/enums/cuss-device-status.enum';
import { NEW_RELIC_EVENT } from '@app/enums/new-relic.enum';
import { CussService } from '@app/services/cuss.service';
import { NewRelicEventsService } from '@app/services/new-relic-events.service';
import {
  BagtagLowPaperModalComponent,
  BagtagPaperCondition,
} from '@modals/bagtag-low-paper-modal/bagtag-low-paper-modal.component';

/** Métrica y código CUSS asociados a cada condición de papel. */
const CONDITION_TELEMETRY: Record<BagtagPaperCondition, { event: string; statusCode: number }> = {
  low: {
    event: NEW_RELIC_EVENT.BAGTAG_PRINTER_MEDIA_LOW,
    statusCode: CussStatusCode.MEDIA_LOW,
  },
  empty: {
    event: NEW_RELIC_EVENT.BAGTAG_PRINTER_MEDIA_EMPTY,
    statusCode: CussStatusCode.MEDIA_EMPTY,
  },
};

/** Superficie mínima del `Device` del SDK usada para la consulta formal de estado. */
interface BagtagPrinterDeviceLike {
  statusIsOK?: (returnPromise: boolean) => Promise<unknown> | null;
  getLastDeviceStatus?: () => unknown;
}

/**
 * Única fuente de verdad, en memoria, del episodio de papel de la impresora Bag Tag:
 * papel bajo `107` (CI-22506) y papel vacío `108` (CVT20-14314). Ambas condiciones
 * comparten modal, CTA, reglas de Home y recuperación; solo cambia el copy.
 *
 * Estado del episodio:
 * - `condition` : `low` (107) o `empty` (108); `null` si no hay episodio.
 * - `active`    : existe un episodio de papel abierto.
 * - `pending`   : el aviso debe mostrarse al llegar a Home.
 * - `shown`     : el modal ya se presentó dentro de este episodio.
 * - `metricSent`: la métrica ya se emitió dentro de este episodio.
 * - `modalRef`  : única referencia al modal abierto.
 *
 * No hay persistencia de ningún tipo (localStorage / sessionStorage / IndexedDB /
 * backend). Tras un reinicio de la app el estado se pierde y Home vuelve a
 * consultar el estado real del dispositivo mediante la API pública del SDK.
 *
 * Este servicio NO se conecta al dispositivo. La recepción de eventos vive en
 * `bagtag.callback.ts`, asignado por `CussService` (standby) y por
 * `BagtagprinterDeviceService.enable()` (activo); ambos delegan aquí mediante
 * `notifyMediaLow()` / `notifyMediaEmpty()` / `notifyMediaRecovered()`.
 */
@Injectable({
  providedIn: 'root',
})
export class BagtagMediaLowService implements OnDestroy {
  private active = false;
  private pending = false;
  private shown = false;
  private metricSent = false;

  /**
   * Condición de papel vigente (`low` = 107, `empty` = 108), o `null` si no hay
   * episodio. Es el único discriminante entre ambos avisos: decide el copy del
   * modal y la métrica, y detecta el cambio de condición `LOW → EMPTY`.
   */
  private condition: BagtagPaperCondition | null = null;

  /** Única referencia al modal abierto. */
  private modalRef: NgbModalRef | null = null;

  /** Home está completamente visible (montada y sin flujo encima). */
  private homeVisible = false;

  /** Espera —sin polling— a que se cierren otros modales para presentar el aviso. */
  private activeInstancesSub: Subscription | null = null;

  constructor(
    private readonly cussService: CussService,
    private readonly ngbModal: NgbModal,
    private readonly zone: NgZone,
    private readonly newRelicEvents: NewRelicEventsService
  ) {}

  // ─── Estado observable (solo lectura, para pruebas y diagnóstico) ────────────

  /** Existe un episodio de papel bajo abierto. */
  get isEpisodeActive(): boolean {
    return this.active;
  }

  /** El aviso quedó diferido y debe mostrarse al volver a Home. */
  get isPresentationPending(): boolean {
    return this.pending;
  }

  /** El modal ya se presentó dentro del episodio actual. */
  get wasShownInEpisode(): boolean {
    return this.shown;
  }

  /** El modal está abierto en este momento. */
  get isModalOpen(): boolean {
    return this.modalRef !== null;
  }

  /** Condición de papel vigente, o `null` si no hay episodio. */
  get currentCondition(): BagtagPaperCondition | null {
    return this.condition;
  }

  ngOnDestroy(): void {
    this.teardownActiveInstancesWatch();
  }

  // ─── Entradas desde los callbacks del dispositivo ────────────────────────────

  /**
   * `107 MEDIA_LOW` recibido por cualquiera de los dos callbacks (activo o
   * compartido). Idempotente: mientras el episodio siga activo, los avisos
   * duplicados no abren, reemplazan ni apilan modal, no duplican métrica y no
   * reinician el estado.
   */
  notifyMediaLow(): void {
    this.startEpisode('low');
  }

  /**
   * `108 MEDIA_EMPTY` recibido por cualquiera de los dos callbacks. Sigue el mismo
   * comportamiento que `107` —modal informativo con CTA, no bloqueante, sin OOS—
   * cambiando únicamente el copy. Idempotente frente a `108` repetidos.
   */
  notifyMediaEmpty(): void {
    this.startEpisode('empty');
  }

  /**
   * `0 OK` recibido por cualquiera de los dos callbacks. Cierra el modal si está
   * abierto, cancela cualquier presentación diferida y reinicia el episodio para
   * permitir que un futuro `107`/`108` vuelva a avisar. Idempotente.
   */
  notifyMediaRecovered(): void {
    this.endEpisode();
  }

  /**
   * Consulta formal del estado real al entrar completamente a Home.
   *
   * Usa `statusIsOK(true)` (Promise del SDK) y, si no está disponible, recurre a
   * `getLastDeviceStatus()`. Un rechazo, timeout, ausencia de dispositivo o
   * respuesta inválida es inerte: no crea episodio, no limpia uno existente, no
   * cambia banderas, no muestra ni cierra el modal y no propaga excepciones.
   */
  queryDeviceStatus(): void {
    const device = this.getDevice();
    if (!device) {
      return;
    }

    try {
      const promise = typeof device.statusIsOK === 'function' ? device.statusIsOK(true) : null;
      if (promise && typeof promise.then === 'function') {
        promise.then(
          (response: unknown) =>
            this.zone.run(() => applyBagtagStatusCode(parseBagtagStatusIsOK(response), this)),
          () => {
            /* Rechazo o timeout: sin efecto sobre el estado. */
          }
        );
        return;
      }
    } catch {
      /* Consulta rechazada: sin efecto sobre el estado. */
    }

    try {
      if (typeof device.getLastDeviceStatus === 'function') {
        const code = Number(device.getLastDeviceStatus());
        applyBagtagStatusCode(Number.isFinite(code) ? code : null, this);
      }
    } catch {
      /* Sin efecto sobre el estado. */
    }
  }

  // ─── Señales de Home ─────────────────────────────────────────────────────────

  /**
   * Home quedó completamente visible: se consulta el estado real del dispositivo
   * y se desahoga cualquier aviso diferido.
   */
  notifyHomeVisible(): void {
    this.homeVisible = true;
    this.queryDeviceStatus();
    this.presentIfPossible();
  }

  /** Home dejó de estar visible (navegación hacia un flujo). */
  notifyHomeHidden(): void {
    this.homeVisible = false;
    this.teardownActiveInstancesWatch();
  }

  // ─── Transiciones del episodio ───────────────────────────────────────────────

  /**
   * Abre un episodio para la condición indicada.
   *
   * - Mientras la condición vigente sea la misma, los avisos duplicados son
   *   completamente inertes: no abren, reemplazan ni apilan modal, no reabren
   *   tras `Continuar`, no duplican métrica y no reinician el estado.
   * - Un cambio de condición (`LOW → EMPTY`, o `EMPTY → LOW`) refleja el estado
   *   más reciente del papel: sustituye al anterior y vuelve a poder avisarse una
   *   sola vez, aunque el aviso previo ya se hubiera mostrado o siguiera pendiente.
   */
  private startEpisode(condition: BagtagPaperCondition): void {
    if (this.condition === condition) {
      return;
    }
    // Si el aviso de la condición anterior seguía abierto, se retira para que el
    // estado vigente no quede oculto detrás de uno obsoleto.
    this.closeModal();
    this.condition = condition;
    this.active = true;
    this.pending = true;
    this.shown = false;
    this.metricSent = false;
    this.presentIfPossible();
  }

  /**
   * `0 OK` válido: cierra el modal si está abierto, cancela cualquier presentación
   * diferida y reinicia todas las banderas —incluida la condición— para permitir un
   * episodio futuro de cualquier tipo. No navega y no crea otro modal.
   */
  private endEpisode(): void {
    this.teardownActiveInstancesWatch();
    this.closeModal();
    this.condition = null;
    this.active = false;
    this.pending = false;
    this.shown = false;
    this.metricSent = false;
  }

  // ─── Presentación ────────────────────────────────────────────────────────────

  /**
   * Presenta el aviso solo si el episodio sigue activo, aún no se mostró en este
   * episodio, Home está completamente visible y no hay otro modal interfiriendo.
   * En cualquier otro caso el aviso queda pendiente sin interrumpir nada.
   */
  private presentIfPossible(): void {
    if (!this.active || this.shown || this.modalRef) {
      return;
    }
    if (!this.homeVisible) {
      this.pending = true;
      return;
    }
    if (this.ngbModal.hasOpenModals()) {
      // Otro modal está abierto: se conserva como pendiente y se espera —sin
      // polling— a que la pila de modales quede libre.
      this.pending = true;
      this.watchActiveInstances();
      return;
    }
    this.openModal();
  }

  /**
   * Abre la única instancia del modal y emite la métrica de presentación.
   *
   * Se envuelve en `NgZone.run` porque los callbacks CUSS llegan fuera de la zona
   * de Angular; al reentrar, la detección de cambios se dispara sola al vaciarse
   * la cola de microtareas (no hace falta forzar `ApplicationRef.tick()`).
   */
  private openModal(): void {
    this.zone.run(() => {
      try {
        this.modalRef = this.ngbModal.open(BagtagLowPaperModalComponent, {
          centered: true,
          size: 'lg',
          backdrop: 'static',
          keyboard: false,
        });
      } catch {
        // Si el modal no pudo abrirse, el aviso permanece pendiente y no se emite
        // métrica: el episodio nunca fue visible.
        this.modalRef = null;
        this.pending = true;
        return;
      }

      // El copy es lo único que distingue papel bajo de papel vacío.
      (this.modalRef.componentInstance as BagtagLowPaperModalComponent).condition =
        this.condition ?? 'low';

      this.shown = true;
      this.pending = false;
      this.teardownActiveInstancesWatch();

      // `Continuar` y `X` terminan aquí: acusan y cierran, sin navegar y sin
      // alterar ningún flujo. `active` y `shown` se conservan, de modo que el
      // modal no puede volver a mostrarse dentro del mismo episodio.
      // Se ata al ref concreto: al sustituir el aviso (p. ej. LOW → EMPTY) la
      // promesa del modal anterior resuelve después y no debe borrar el nuevo.
      const openedRef = this.modalRef;
      openedRef.result.then(
        () => this.onModalClosed(openedRef),
        () => this.onModalClosed(openedRef)
      );

      this.trackPresentation();
    });
  }

  /** Cierra el modal si está abierto. Idempotente. */
  private closeModal(): void {
    if (!this.modalRef) {
      return;
    }
    const ref = this.modalRef;
    this.modalRef = null;
    this.zone.run(() => {
      try {
        ref.dismiss();
      } catch {
        /* El modal ya estaba cerrado. */
      }
    });
  }

  private onModalClosed(ref: NgbModalRef): void {
    if (this.modalRef === ref) {
      this.modalRef = null;
    }
  }

  /**
   * Métrica de presentación: se emite cuando el modal se vuelve realmente visible,
   * una sola vez por episodio. Un fallo de New Relic no altera estado, modal ni flujo.
   */
  private trackPresentation(): void {
    if (this.metricSent) {
      return;
    }
    this.metricSent = true;
    try {
      const { event, statusCode } = CONDITION_TELEMETRY[this.condition ?? 'low'];
      this.newRelicEvents.trackEvent(event, {
        device: 'BAGTAG_PRINTER',
        status_code: statusCode,
      });
    } catch {
      /* Best-effort: sin reintentos, colas ni persistencia. */
    }
  }

  // ─── Utilidades internas ─────────────────────────────────────────────────────

  /**
   * Se suscribe a `NgbModal.activeInstances` para reintentar la presentación en
   * cuanto la pila de modales quede vacía. Es la API disponible en la versión
   * instalada; evita cualquier polling.
   */
  private watchActiveInstances(): void {
    if (this.activeInstancesSub) {
      return;
    }
    this.activeInstancesSub = this.ngbModal.activeInstances.subscribe((instances) => {
      if (instances.length === 0) {
        this.presentIfPossible();
      }
    });
  }

  private teardownActiveInstancesWatch(): void {
    this.activeInstancesSub?.unsubscribe();
    this.activeInstancesSub = null;
  }

  /** Resuelve el device Bag Tag Printer; `null` cuando no hay dispositivo. */
  private getDevice(): BagtagPrinterDeviceLike | null {
    try {
      const device = this.cussService.getDeviceManager()?.getDevice(deviceIds.BAGTAG_PRINTER);
      return (device as unknown as BagtagPrinterDeviceLike) ?? null;
    } catch {
      return null;
    }
  }
}
